<template>
  <div>
    <SideSheet
      v-if="portfolio"
      :is-open="isOpen"
      position="right"
      class="wealth-config"
      data-testid="wealth-config"
      @close="emitCloseConfig"
    >
      <NavigationPanelWrapper>
        <NavigationPanel>
          <div slot-scope="{ setPanel }">
            <NavBar>
              <template #center>Configurações</template>
              <template #right>
                <ButtonIcon
                  :colors="closeButtonColors"
                  label="Fechar"
                  icon="EF0030"
                  @click="emitCloseConfig"
                />
              </template>
            </NavBar>
            <div class="wealth-config__content">
              <div class="wealth-config__content__background">
                <BaseCard
                  class="wealth-config__content__background-card"
                  :background="backgroundImage || portfolio.background.thumb"
                  @click="setPanel(1)"
                >
                  <template #bottom>
                    <ul>
                      <div class="wealth-config__content__background-image">
                        <img
                          src="https://warren.com.br/assets/imgs/icons/ic_camera_24px_white.svg"
                          alt="Alterar Foto da Carteira"
                        />
                      </div>
                    </ul>
                  </template>
                </BaseCard>
              </div>
              <div class="wealth-config__content__options">
                <Row
                  v-if="!isEmergencyFundPortfolio"
                  chevron
                  has-interaction
                  @click="portfolioNameEditClick"
                >
                  <template #top-left>{{ portfolio.name }}</template>
                  <template #bottom-left>Nome da carteira</template>
                </Row>
                <Row chevron has-interaction @click="reminderClick">
                  <template #top-left>
                    {{ reminderMessageButton }}
                  </template>
                  <template #bottom-left>Receba um lembrete mensal</template>
                </Row>
              </div>
              <Row chevron has-interaction @click="deleteClick">
                <template #top-left>
                  <BaseText
                    :color="NebraskaColors.elementPrimary"
                    size="p3"
                    class="wealth-config__delete-button"
                  >
                    Deletar carteira
                  </BaseText>
                </template>
              </Row>
            </div>
          </div>
        </NavigationPanel>
        <NavigationPanel>
          <div
            slot-scope="{ setPanel }"
            data-testid="wealth-config-background-panel"
          >
            <NavBar class="wealth-config__background-navbar">
              <template #left>
                <ButtonIcon
                  :colors="closeButtonColors"
                  label="Fechar"
                  icon="EA0030"
                  @click="setPanel(0)"
                />
              </template>
              <template #center>
                Fotos do
                <a src="https://unsplash.com/">
                  <u>Unsplash</u>
                </a>
              </template>
            </NavBar>
            <WealthPortfolioBackground
              :portfolio-id="portfolio.id"
              @background-image-changed="onBackgroundImageChanged"
            />
          </div>
        </NavigationPanel>
      </NavigationPanelWrapper>
    </SideSheet>

    <RenamePortfolioModal
      :is-open="isPortfolioEditOpen"
      :portfolio-name="portfolio.name"
      @close="handleEditPortfolioNameModal"
      @change-name="savePortfolioName"
    />

    <InvestReminderModal
      :data="reminderModalProps"
      @close="handleReminderModal"
      @save="saveInvestReminder"
    />

    <DeletePortfolioModal
      :is-open="isDeleteModalOpen"
      :is-not-allowed="isNotAllowed"
      @close="handleDeleteModal"
      @delete="deletePortfolio"
    />
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import {
  NavBar,
  ButtonIconFoundation as ButtonIcon,
  BaseText,
  Row
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { RenamePortfolioModal } from './components/RenamePortfolioModal';
import { DeletePortfolioModal } from './components/DeletePortfolioModal';
import {
  NavigationPanelWrapper,
  NavigationPanel
} from '@/modules/common/components/NavigationPanel';
import {
  IReminderModalProps,
  InvestReminderModal
} from './components/InvestReminderModal';
import { SideSheet } from '@/modules/common/components/SideSheet';
import { BaseCard } from '@/modules/common/components/cards/BaseCard';
import { WealthPortfolioBackground } from '@/modules/wealth/components/WealthPortfolioBackground';
import { GoalType } from '@/types/models/Portfolio';
import { update, rename, remove } from '@/modules/wealth/services/portfolio';
import { moneyViewer as money } from '@/modules/common/helpers/currency';
import { IWealthPortfolio } from '@/modules/wallet-builder/src/services/types/models/IBffInvestments';

@Component({
  components: {
    NavBar,
    ButtonIcon,
    BaseText,
    Row,
    RenamePortfolioModal,
    DeletePortfolioModal,
    BaseCard,
    NavigationPanelWrapper,
    NavigationPanel,
    SideSheet,
    WealthPortfolioBackground,
    InvestReminderModal
  }
})
export default class WealthConfig extends Vue {
  readonly NebraskaColors = NebraskaColors;

  @Prop({ type: Object, required: true })
  readonly portfolio!: IWealthPortfolio;

  @Prop({ type: Boolean, default: false })
  readonly isOpen!: boolean;

  @Emit('close-config')
  emitCloseConfig() {}

  @Emit('save')
  private emitSave() {}

  backgroundImage = '';
  isPortfolioEditOpen = false;
  isDeleteModalOpen = false;
  isNotAllowed = false;
  reminderModalProps = {
    title: '',
    isOpen: false,
    investReminderDay: '',
    investReminder: false,
    monthlyInvestment: 0
  };

  private setModalValues() {
    this.reminderModalProps = {
      investReminderDay: this.portfolio.investReminderDay.toString(),
      investReminder: this.portfolio.investReminder,
      monthlyInvestment: this.portfolio.monthlyInvestmentGoal,
      title: 'Lembrete',
      isOpen: false
    };
  }

  created() {
    this.setModalValues();
  }

  onBackgroundImageChanged(photo: string) {
    this.backgroundImage = photo;
    this.emitCloseConfig();
  }

  get reminderMessageButton() {
    if (!this.portfolio.investReminder) return 'Lembrete desativado';

    return `Todo dia ${this.portfolio.investReminderDay} no valor de ${money(
      this.portfolio.monthlyInvestmentGoal
    )}`;
  }

  get isEmergencyFundPortfolio() {
    return this.portfolio.goal === GoalType.EMERGENCY_FUND;
  }

  get closeButtonColors() {
    return {
      background: NebraskaColors.elementSecondary,
      content: NebraskaColors.elementPrimary,
      alphas: {
        default: 0,
        hover: 0.08,
        active: 0.08
      }
    };
  }

  handleEditPortfolioNameModal() {
    this.isPortfolioEditOpen = !this.isPortfolioEditOpen;
  }

  handleReminderModal() {
    this.reminderModalProps.isOpen = !this.reminderModalProps.isOpen;
  }

  portfolioNameEditClick() {
    this.setModalValues();
    this.handleEditPortfolioNameModal();
  }

  reminderClick() {
    this.setModalValues();
    this.handleReminderModal();
  }

  deleteClick() {
    this.isNotAllowed = false;
    this.handleDeleteModal();
  }

  handleDeleteModal() {
    this.isDeleteModalOpen = !this.isDeleteModalOpen;
  }

  async savePortfolioName(name: string) {
    try {
      await rename(this.portfolio.id, name);
      this.emitSave();
      this.isPortfolioEditOpen = false;
    } catch (error) {
      console.error(error);
    }
  }

  async saveInvestReminder({
    investReminderDay,
    investReminder,
    monthlyInvestment
  }: IReminderModalProps) {
    try {
      await update({
        investReminderDay: Number(investReminderDay),
        _id: this.portfolio.id,
        investReminder,
        skipInvestReminder: false,
        monthlyInvestment,
        initialInvestment: monthlyInvestment
      });
      this.emitSave();
      this.reminderModalProps.isOpen = false;
    } catch (error) {
      console.error(error);
    }
  }

  async deletePortfolio() {
    try {
      await remove(this.portfolio.id);
      this.$router.push({ name: 'wealth' });
    } catch (error) {
      this.isNotAllowed = true;
      console.error(error);
    }
  }
}
</script>

<style lang="less" scoped>
.wealth-config {
  /deep/ .panel-content {
    width: 400px;
  }

  &__content {
    padding: @size-spacing-x600;

    &__background {
      padding-bottom: @size-spacing-x600;

      &-card {
        height: 200px;
      }

      &-image {
        float: right;
      }
    }

    &__options {
      padding-bottom: @size-spacing-x600;
    }
  }

  &__delete-button {
    font-weight: 600;
  }

  /deep/ .wealth-portfolio-background__container {
    margin-top: @size-spacing-x1400;
  }
}

/deep/ .base-modal--wrapper {
  min-width: 400px;
}
</style>
